import {
  currentTemperatureUrl,
  nodeTreeUrl,
  dataUrl,
  installationsOnNodeUrl,
  currentRelativeHumidityUrl,
  getNodeWithParentsUrl,
  billingResultsUrl,
} from "../common/infrastructure/endpoints";
import { actionCreator } from "./nodeDetails.reducer";
import { get } from "../common/infrastructure/requests";
import { INode } from "../search/types";
import {
  IInstallationData,
  INodeTree,
  IUnixTimeObject,
  ISelectedInstallation,
  IMeasuringPoint,
  UtlType,
  IInstallationDataResult,
  IBillingResult,
} from "./types";
import { dataInterval, DeviceType } from "../common/types";
import {
  Grouping,
  IUtility,
  IInstallation,
  IInstallationExtended,
} from "./types";
import {
  getClimateSelectedInstallation,
  getConsumptionSelectedInstallation,
  calculateMonthCostAndEmission,
  getDisplayNameOnDeviceType,
  utlAndAggregateOnUtlType,
  getLatestRealValueForIndoorClimate,
  convertDataIfFromDateNotAllowed as convertDataIfFromDateNotAllowed,
  convertIfUnitM3,
  setSelectedInstallationWithEmptyData,
  getSumOfAllMeasuringPoints,
  convertDataGToKg,
  sortDataResults,
  getUtilityOnDeviceType,
  identifyDeviceTypeOnWater,
  setUtility,
} from "../nodeDetails/functions/dataFunctions";
import { isLandlordTypeGuard } from "common/models/userInfo";
import { IApplicationState } from "redux/root-reducer";
import { DateToUnixConverter } from "common/DateConverter";
import moment from "moment";
import {
  getDateYearsPrior,
  getSumOfDataset,
  utlAndAggregateOnUtlTypeWithPriceAndCo2,
  isInstallationSensor,
} from "nodeDetails/functions/dataFunctions";
import {
  correctTimeSlot,
  getOneIntervalLater,
} from "./functions/dateFunctions";

export const getCurrentTemperature = async (domain: string, id: number) => {
  try {
    const body = await get<IInstallationData[]>(
      currentTemperatureUrl(domain, id)
    );

    if (body.length === 0) {
      return;
    }
    return body as IInstallationData[];
  } catch (e) {}
};
export const getCurrentRelativeHumidity = async (
  domain: string,
  id: number
) => {
  try {
    const body = await get<IInstallationData[]>(
      currentRelativeHumidityUrl(domain, id)
    );
    if (body.length === 0) {
      return;
    }
    return body;
  } catch (e) {}
};

export const getNodeInfoAfterRefreshingPage =
  (domain: string, id: number) => async (dispatch: ({}) => void) => {
    try {
      dispatch(actionCreator.fetching);
      const node = await get<INode>(getNodeWithParentsUrl(domain, id));
      if (!node) return;
      dispatch(actionCreator.setSelectedNode(node));
    } catch (e) {
      dispatch(actionCreator.setInstallationsError);
    }
  };

export const getAddressForNode =
  (domain: string, id: string) => async (dispatch: ({}) => void) => {
    try {
      const nodeTree = await get<INodeTree>(nodeTreeUrl(domain, id));
      if (!nodeTree) return;
      dispatch(actionCreator.setNodeTree(nodeTree));
    } catch (e) {
      dispatch(actionCreator.setInstallationsError);
    }
  };
export const clearTemperatureHistory = () => async (dispatch: ({}) => void) => {
  dispatch(actionCreator.clearTemperatureHistory());
};

export const getInstallations =
  (domain: string, nodeId: number) =>
  async (dispatch: ({}) => void, getState: () => IApplicationState) => {
    const state = getState();
    const occupationDate = isLandlordTypeGuard(state.common.currentUserInfo)
      ? 0
      : state.common.currentUserInfo.OccupationDate || 0;

    dispatch(actionCreator.fetching);
    try {
      let installations = await get<IInstallation[]>(
        installationsOnNodeUrl(domain, nodeId)
      );
      if (!installations) {
        dispatch(actionCreator.setInstallationsError);
      }

      const user = getState().common.currentUserInfo;
      const currency = user.DomainSettings.Currency;
      if (!isLandlordTypeGuard(user)) {
        installations = installations.filter((i) => {
          if (!user.DomainSettings.TenantAccessStatus.canViewTemperature) {
            if (i.DeviceType === DeviceType.Climate) return;
          }
          if (!user.DomainSettings.TenantAccessStatus.canViewMeters) {
            if (i.DeviceType === DeviceType.ShellProtection) return;
          }
          return i;
        });
      }
      if (installations.some((i) => i.DeviceType === DeviceType.Climate)) {
        const currentTemp = await getCurrentTemperature(domain, nodeId);
        if (currentTemp) {
          const latestValue = getLatestRealValueForIndoorClimate(
            currentTemp[0].Result
          );
          latestValue &&
            dispatch(
              actionCreator.fetchingCurrentTemp(+latestValue.Value.toFixed(1))
            );
        }
      }
      const currentRelativeHumidity = await getCurrentRelativeHumidity(
        domain,
        nodeId
      );
      if (currentRelativeHumidity) {
        const latestValue = getLatestRealValueForIndoorClimate(
          currentRelativeHumidity[0].Result
        );
        latestValue &&
          dispatch(
            actionCreator.fetchingCurrentRelativeHumidity(
              +latestValue.Value.toFixed(1)
            )
          );
      }
      installations = installations.map((i) => {
        if (i.DeviceType !== DeviceType.Water) return i;
        return {
          ...i,
          DeviceType: identifyDeviceTypeOnWater(i),
        };
      });
      const relevantInstallationsToExtend = installations
        .filter((i) => !isInstallationSensor(i))
        .reduce((acc: IInstallation[], current) => {
          if (acc.find((x) => x.DeviceType === current.DeviceType)) return acc;

          return [...acc, current];
        }, []);
      const installationsExtended = await extendInstallations(
        relevantInstallationsToExtend,
        domain,
        nodeId,
        occupationDate
      );

      if (getState().common.includeVAT) {
        installationsExtended.forEach((e) => {
          e.data = addVAT(e.data);
        });
        installationsExtended.forEach(
          (e) => (e.currentMonthCost = e.currentMonthCost * 1.25)
        );
      }

      const { totalMonthCost, totalMonthEmission } =
        calculateMonthCostAndEmission(installationsExtended);

      dispatch(
        actionCreator.setInstallations({
          installationsExtended,
          totalMonthCost,
          totalMonthEmission,
          currency,
        })
      );
    } catch (e) {
      dispatch(actionCreator.setInstallationsError);
    }
  };

function addVAT(data: IInstallationDataResult[]) {
  data.forEach((e) => {
    if (e.Func === "price") {
      e.Values = e.Values.map((j) => ({ ...j, Value: j.Value * 1.25 }));
    }
  });
  return data;
}

export const setSelectedInstallation =
  (
    domain: string,
    nodeId: number,
    deviceType: DeviceType,
    period: IUnixTimeObject,
    interval: dataInterval,
    yearsBack: number = 1,
    measuringPointID?: number
  ) =>
  async (dispatch: ({}) => void, getState: () => IApplicationState) => {
    const state = getState();
    const occupationDate = isLandlordTypeGuard(state.common.currentUserInfo)
      ? 0
      : state.common.currentUserInfo.OccupationDate || 0;
    const installations = state.node.installations;
    let installation = installations?.find((i) => i.DeviceType === deviceType);

    if (!installation) return;
    let utlAndAggregate = getUtilityOnDeviceType(installation.DeviceType);
    if (utlAndAggregate[0].utlType === UtlType.HeatCostAllocator)
      utlAndAggregate[0].utlType = installation.Registers.some(
        (e) => e.UtilityCode === UtlType.HeatCostAllocator
      )
        ? UtlType.HeatCostAllocator
        : UtlType.HeatCostAllocatorE;

    try {
      dispatch(actionCreator.fetching);

      const displayName = getDisplayNameOnDeviceType(installation.DeviceType);
      const from = correctTimeSlot(occupationDate, period.from, interval);
      const to = getOneIntervalLater(period.from, period.period);

      if (from >= to) {
        dispatch(actionCreator.resetSelectedInstallation);
        return;
      }

      let data = await get<IInstallationData[]>(
        dataUrl(
          domain,
          nodeId,
          from,
          to,
          interval,
          utlAndAggregate,
          Grouping.MeasuringPoint
        )
      );

      if (getState().common.includeVAT) {
        data.forEach((e) => {
          e.Result = addVAT(e.Result);
        });
      }

      if (!data.length) {
        dispatch(
          setSelectedInstallationWithEmptyData(
            deviceType,
            period,
            displayName,
            yearsBack,
            domain,
            nodeId,
            interval,
            utlAndAggregate
          )
        );
        return;
      }
      data = sortDataResults(data);
      data = data.map((result) =>
        convertDataIfFromDateNotAllowed(result, period)
      );
      if (deviceType === DeviceType.Climate) {
        const selectedClimateInstallation = getClimateSelectedInstallation(
          data[0].Result,
          displayName
        );
        dispatch(
          actionCreator.setSelectedInstallation(selectedClimateInstallation)
        );
      } else {
        const measuringPoints: IMeasuringPoint[] = data.map((d) => {
          return { ID: d.ID, Name: d.Name };
        });
        if (measuringPointID) {
          const installationData = data.find((d) => d.ID === measuringPointID);
          if (!installationData) {
            setSelectedInstallationWithEmptyData(
              deviceType,
              period,
              displayName,
              yearsBack,
              domain,
              nodeId,
              interval,
              utlAndAggregate
            );
            return;
          }
          dispatch(
            setSelectedInstallationWithMeasuringPoint(
              installationData,
              domain,
              nodeId,
              period,
              interval,
              yearsBack,
              measuringPointID,
              occupationDate,
              utlAndAggregate,
              displayName,
              measuringPoints
            )
          );
          return;
        }
        if (deviceType !== DeviceType.HeatCostAllocator) {
          data = data.map((d) => convertIfUnitM3(d));
          data = data.map((d) => convertDataGToKg(d));
        }

        let comparisonFrom = getDateYearsPrior(period.from, yearsBack);
        const comparisonTo = getDateYearsPrior(
          getOneIntervalLater(period.from, period.period),
          yearsBack
        );
        if (comparisonTo > occupationDate) {
          comparisonFrom = correctTimeSlot(
            occupationDate,
            comparisonFrom,
            interval
          );

          let comparisonData: IInstallationData[];
          if (comparisonFrom >= comparisonTo) comparisonData = [];
          else
            comparisonData = await get<IInstallationData[]>(
              dataUrl(
                domain,
                nodeId,
                comparisonFrom,
                comparisonTo,
                interval,
                utlAndAggregate,
                Grouping.MeasuringPoint
              )
            );

          if (getState().common.includeVAT) {
            comparisonData.forEach((e, i) => {
              e.Result = addVAT(e.Result);
            });
          }

          comparisonData = sortDataResults(comparisonData);
          comparisonData = comparisonData.map((d) =>
            convertDataIfFromDateNotAllowed(d, period)
          );
          if (deviceType !== DeviceType.HeatCostAllocator) {
            comparisonData = comparisonData.map((d) => convertIfUnitM3(d));
            comparisonData = comparisonData.map((d) => convertDataGToKg(d));
          }
          data[0].Result = getSumOfAllMeasuringPoints(data);
          const comparisonResult =
            comparisonData.length === 0
              ? undefined
              : getSumOfAllMeasuringPoints(comparisonData);
          const setSelectedInstallation: ISelectedInstallation =
            getConsumptionSelectedInstallation(
              data[0].Result,
              displayName,
              measuringPoints,
              period.period,
              comparisonResult
            );
          dispatch(
            actionCreator.setSelectedInstallation(setSelectedInstallation)
          );
          return;
        }

        if (measuringPointID) {
          const setSelectedInstallation: ISelectedInstallation =
            getConsumptionSelectedInstallation(
              data[0].Result,
              displayName,
              measuringPoints,
              period.period
            );

          dispatch(
            actionCreator.setSelectedInstallation(setSelectedInstallation)
          );
          return;
        }

        data[0].Result = getSumOfAllMeasuringPoints(data);
        const setSelectedInstallation: ISelectedInstallation =
          getConsumptionSelectedInstallation(
            data[0].Result,
            displayName,
            measuringPoints,
            period.period,
            undefined
          );
        dispatch(
          actionCreator.setSelectedInstallation(setSelectedInstallation)
        );
      }
    } catch (e) {
      dispatch(actionCreator.resetSelectedInstallation);
      throw e;
    }
  };

export const extendInstallations = async (
  installations: IInstallation[],
  domain: string,
  nodeId: number,
  occupationDate: number
) => {
  const todaysDate = moment();
  const conDataPromise = async (installation: IInstallation) => {
    const requestedData = setUtility(
      installation,
      utlAndAggregateOnUtlTypeWithPriceAndCo2
    );

    let consumptionData = await get<IInstallationData[]>(
      dataUrl(
        domain,
        nodeId,
        DateToUnixConverter(moment(todaysDate).startOf("month")),
        DateToUnixConverter(moment(todaysDate).endOf("month").add(1, "second")),
        dataInterval.Day,
        requestedData,
        Grouping.Apartment
      )
    );
    if (installation.DeviceType === DeviceType.Climate) {
      return [
        {
          ...installation,
          data: consumptionData[0].Result,
          currentMonthCost: 0,
          currentMonthEmission: 0,
          comparisonData: undefined,
        },
        consumptionData,
      ];
    }

    const requestedComparisonData = setUtility(
      installation,
      utlAndAggregateOnUtlType
    );
    const comparisonDataFromTimestamp = DateToUnixConverter(
      moment(todaysDate).subtract(1, "month").startOf("month")
    );
    const comparisonDataToTimestamp = DateToUnixConverter(
      moment(todaysDate).subtract(1, "month").endOf("month").add(1, "second")
    );

    let comparisonData;
    if (comparisonDataFromTimestamp > occupationDate) {
      comparisonData = await get<IInstallationData[]>(
        dataUrl(
          domain,
          nodeId,
          comparisonDataFromTimestamp,
          comparisonDataToTimestamp,
          dataInterval.Day,
          requestedComparisonData,
          Grouping.Apartment
        )
      );
    }

    const tuple: [
      IInstallation,
      IInstallationData[],
      IInstallationData[] | undefined
    ] = [installation, consumptionData, comparisonData];
    return tuple;
  };
  const conDatapromises = installations.map((ri) => conDataPromise(ri));
  let extendedList: IInstallationExtended[] = [];
  await Promise.all(conDatapromises)
    .then((result) => {
      for (let res of result) {
        //ts 3.5 has no support to infer tuple types as such
        let installation = res[0] as IInstallation;
        let consumptionData = res[1] as IInstallationData[];

        consumptionData = sortDataResults(consumptionData);
        consumptionData[0] = convertIfUnitM3(consumptionData[0]);

        const priceValues =
          consumptionData[0].Result.find((r) => r.Func === "price")?.Values ??
          [];
        const emissionValues =
          consumptionData[0].Result.find((r) => r.Func === "co2")?.Values ?? [];

        const price = getSumOfDataset(priceValues);
        const emission = getSumOfDataset(emissionValues);

        let attemptExtended: IInstallationExtended = {
          ...installation,
          data: consumptionData[0].Result,
          currentMonthCost: price,
          currentMonthEmission: emission,
          comparisonData: undefined,
        };

        if (typeof res[2] !== "undefined") {
          let comparisonData = res[2] as IInstallationData[];
          comparisonData = sortDataResults(comparisonData);
          comparisonData[0] = convertIfUnitM3(comparisonData[0]);

          attemptExtended = {
            ...attemptExtended,
            comparisonData: comparisonData[0].Result,
          };
        }
        extendedList = [...extendedList, attemptExtended];
      }
    })
    .catch((e) => {
      console.error(e.message);
    });
  return extendedList;
};

export const setSelectedInstallationWithMeasuringPoint =
  (
    installationData: IInstallationData,
    domain: string,
    nodeId: number,
    unixTimeObject: IUnixTimeObject,
    interval: dataInterval,
    yearsBack: number = 1,
    measuringPointID: number,
    occupationDate: number,
    utlAndAggregate: IUtility[],
    displayName: string,
    measuringPoints: IMeasuringPoint[]
  ) =>
  async (dispatch: ({}) => void) => {
    installationData = convertDataIfFromDateNotAllowed(
      installationData,
      unixTimeObject
    );
    if (
      installationData.Result[0].Utl !== UtlType.HeatCostAllocator &&
      installationData.Result[0].Utl !== UtlType.HeatCostAllocatorE
    ) {
      installationData = convertDataGToKg(installationData);
      installationData = convertIfUnitM3(installationData);
    }

    let comparisonFrom1 = getDateYearsPrior(unixTimeObject.from, yearsBack);
    const comparisonTo1 = getDateYearsPrior(
      getOneIntervalLater(unixTimeObject.from, unixTimeObject.period),
      yearsBack
    );
    if (comparisonTo1 > occupationDate) {
      comparisonFrom1 = correctTimeSlot(
        occupationDate,
        comparisonFrom1,
        interval
      );

      if (comparisonFrom1 < comparisonTo1) {
        let comparisonData = await get<IInstallationData[]>(
          dataUrl(
            domain,
            nodeId,
            comparisonFrom1,
            comparisonTo1,
            interval,
            utlAndAggregate,
            Grouping.MeasuringPoint
          )
        );
        let installationComparisonData = comparisonData.find(
          (d) => d.ID === measuringPointID
        );

        if (installationComparisonData) {
          installationComparisonData = convertDataIfFromDateNotAllowed(
            installationComparisonData,
            unixTimeObject
          );
          installationComparisonData = convertIfUnitM3(
            installationComparisonData
          );
        }

        const setSelectedInstallation: ISelectedInstallation =
          getConsumptionSelectedInstallation(
            installationData.Result,
            displayName,
            measuringPoints,
            unixTimeObject.period,
            installationComparisonData?.Result
          );
        dispatch(
          actionCreator.setSelectedInstallation(setSelectedInstallation)
        );
        return;
      }
    }

    const setSelectedInstallation: ISelectedInstallation =
      getConsumptionSelectedInstallation(
        installationData.Result,
        displayName,
        measuringPoints,
        unixTimeObject.period,
        undefined
      );

    dispatch(actionCreator.setSelectedInstallation(setSelectedInstallation));
  };

export const getBillingResults =
  (domain: string, nodeId: string) =>
  async (dispatch: ({}) => void, getState: () => IApplicationState) => {
    try {
      dispatch(actionCreator.setFetchBillingResultsFailed(false));
      dispatch(actionCreator.setFetchingBillingResults(true));

      const state = getState();
      const occupationDate = isLandlordTypeGuard(state.common.currentUserInfo)
        ? 0
        : state.common.currentUserInfo.OccupationDate || 0;

      const responseCorrect = await get<IBillingResult[]>(
        billingResultsUrl(
          domain,
          [nodeId, nodeId],
          undefined,
          undefined,
          occupationDate,
          DateToUnixConverter(moment())
        )
      );

      const response = responseCorrect;

      if (!response) return;
      dispatch(actionCreator.setBillingResults(response));
    } catch (e) {
      dispatch(actionCreator.setFetchBillingResultsFailed(true));
    } finally {
      dispatch(actionCreator.setFetchingBillingResults(false));
    }
  };
